import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Label,
  Avatar,
  Icon,
  Chip,
  Autocomplete,
  AutocompleteItem,
} from 'mw-style-react';
import {
  SEARCH_LAYERS,
  GET_LAYERS,
} from '@control-front-end/common/constants/graphLayers';
import useScroll from 'useScroll';
import useOutsideClick from 'useOutsideClick';
import useIntl from 'useIntl';
import mes from './intl';
import './LayersMultiselect.scss';

/**
 * Множественный выбор слоев
 */
function LayersMultiselect(props) {
  const {
    id,
    value,
    error,
    placeholder,
    exclude = [],
    helperText,
    onChange,
    onBlur,
    label,
  } = props;
  const t = useIntl();
  const dispatch = useDispatch();
  const searchBox = useRef();
  const [query, setQuery] = useState('');
  const [focus, setFocus] = useState(false);
  const initState = {
    list: [],
    limit: 15,
    offset: 0,
    endList: false,
    reqStatus: 'success',
  };
  const [layers, setLayers] = useState(initState);
  const [search, setSearch] = useState([]);

  const getData = (loadMore = false) => {
    dispatch({
      type: GET_LAYERS.REQUEST,
      payload: { loadMore, layersState: layers },
      callback: (data) => setLayers({ ...layers, ...data }),
    });
  };

  /**
   * Обработка снятия фокуса
   */
  const handleBlur = () => {
    setFocus(false);
    setQuery('');
    if (!onBlur) return;
    onBlur({ id, value: query });
  };

  useEffect(() => {
    getData();
  }, []);
  useOutsideClick({ ref: searchBox, callback: handleBlur });
  useScroll({ ref: searchBox, callback: () => getData(true) });

  /**
   * Обработка поискового запроса
   */
  const handleChange = ({ value: q }) => {
    setQuery(q);
    if (!q.length) {
      setSearch([]);
      return;
    }
    if (q.length < 2) return;

    dispatch({
      type: SEARCH_LAYERS.REQUEST,
      payload: { query: q },
      callback: (data) => setSearch(data),
    });
  };

  /**
   * Добавить слой
   */
  const handleAddItem = (item) => {
    const list = value.slice();
    list.push({ ...item, action: 'create' });
    onChange({ id, value: list });
    setFocus(false);
    setQuery('');
    setSearch([]);
  };

  /**
   * Исключить слой
   */
  const handleDelItem = (layerId) => {
    const list = value.slice();
    const index = list.findIndex((i) => i.id === layerId);
    if (index === -1) return;
    const layer = list[index];
    if (layer.action === 'create') {
      list.splice(index, 1);
    } else {
      layer.action = 'delete';
      const item = { ...layer };
      list.splice(index, 1, item);
    }
    onChange({ id, value: list });
    setFocus(false);
    setQuery('');
    setSearch([]);
  };

  const renderChipsItems = () => {
    const selected = value.filter((i) => i.action !== 'delete');
    return selected.map((item) => (
      <Chip
        key={item.id}
        label={item.name}
        size="small"
        type="rectangular"
        fontWeight="semibold"
        bgColor="#F0F6FD"
        borderColor="#BAD5F8"
        closeClick={() => handleDelItem(item.id)}
      />
    ));
  };

  const emptyResult = () => (
    <AutocompleteItem
      id="not_found"
      size="small"
      value="Not found"
      onClick={handleBlur}
    />
  );

  const renderSearchResult = () => {
    if (!focus) return;
    const isSearch = query.length > 2;
    const list = isSearch ? search : layers.list;
    const resultList = list.filter(
      (layer) =>
        !value.find((i) => i.id === layer.id) &&
        !exclude.includes(layer.id) &&
        !layer.isSystem
    );
    const resultLabel = isSearch
      ? `${t(mes.searchResults)}: ${resultList.length}`
      : t(mes.suggestions);

    const items = resultList.map((i) => (
      <AutocompleteItem
        styleName="lms__item"
        key={i.id}
        id={i.id}
        value={i.name}
        onClick={() => handleAddItem(i)}
        children={
          <Avatar styleName="lms__item__avatar">
            <Icon type="layer" size="small" color="#1973E8" />
          </Avatar>
        }
      />
    ));
    return (
      <div>
        <Label
          styleName="lms__title"
          fontWeight="semibold"
          value={resultLabel}
        />
        {items.length ? items : emptyResult()}
      </div>
    );
  };

  return (
    <div ref={searchBox}>
      <Autocomplete
        styleName="lms__field"
        size="large"
        placeholder={placeholder}
        label={label}
        value={query}
        bordered={true}
        error={error}
        helperText={helperText}
        onChange={handleChange}
        onFocus={() => setFocus(true)}
        searchResult={renderSearchResult}
        chipsItems={renderChipsItems}
        portalOptions={{ offsetX: 2 }}
      />
    </div>
  );
}

LayersMultiselect.propTypes = {
  id: PropTypes.string,
  value: PropTypes.array,
  label: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  placeholder: PropTypes.string,
  exclude: PropTypes.array,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};

export default LayersMultiselect;
